import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { SEOService } from "../../services/seoService";
import SEOSettingsForm from "./SEOSettingsForm";
import SEOSettingsList from "./SEOSettingsList";
import SEOSettingsModal from "./SEOSettingsModal";
import "./SEOPortal.css";

const SEOPortal = () => {
  const [seoSettings, setSeoSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSetting, setSelectedSetting] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentView, setCurrentView] = useState("list"); // 'list' or 'form'

  useEffect(() => {
    loadSEOSettings();
  }, []);

  const loadSEOSettings = async () => {
    try {
      setLoading(true);
      const settings = await SEOService.getAllSEOSettings();
      setSeoSettings(settings);
      setError(null);
    } catch (err) {
      setError("Failed to load SEO settings");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setSelectedSetting(null);
    setCurrentView("form");
  };

  const handleEdit = (setting) => {
    setSelectedSetting(setting);
    setCurrentView("form");
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this SEO setting?")) {
      try {
        await SEOService.deleteSEOSettings(id);
        await loadSEOSettings();
      } catch (err) {
        setError("Failed to delete SEO setting");
        console.error(err);
      }
    }
  };

  const handleSave = async (seoData) => {
    try {
      if (selectedSetting) {
        await SEOService.updateSEOSettings(selectedSetting.id, seoData);
      } else {
        await SEOService.createSEOSettings(seoData);
      }
      await loadSEOSettings();
      setCurrentView("list");
      setSelectedSetting(null);
    } catch (err) {
      setError("Failed to save SEO setting");
      console.error(err);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      await loadSEOSettings();
      return;
    }

    try {
      setLoading(true);
      const results = await SEOService.searchSEOSettings(searchTerm);
      setSeoSettings(results);
    } catch (err) {
      setError("Failed to search SEO settings");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredSettings = seoSettings.filter((setting) => {
    if (filterStatus === "all") return true;
    return setting.isActive === (filterStatus === "active");
  });

  return (
    <>
      <Helmet>
        <title>SEO Portal - Polani Travels</title>
        <meta name="description" content="Manage SEO settings for all pages" />
      </Helmet>

      <div className="seo-portal">
        <div className="seo-portal-header">
          <h1>SEO Portal</h1>
          <p>Manage SEO settings for all pages</p>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div className="seo-portal-controls">
          <div className="controls-left">
            <button
              className={`btn ${
                currentView === "list" ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => setCurrentView("list")}
            >
              View All
            </button>
            <button
              className={`btn ${
                currentView === "form" ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={handleCreateNew}
            >
              Add New
            </button>
          </div>

          {currentView === "list" && (
            <div className="controls-right">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search SEO settings..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                />
                <button
                  className="btn btn-outline-secondary"
                  onClick={handleSearch}
                >
                  Search
                </button>
              </div>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="form-select"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          )}
        </div>

        {currentView === "list" ? (
          <SEOSettingsList
            seoSettings={filteredSettings}
            loading={loading}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onView={setSelectedSetting}
            onShowModal={setShowModal}
          />
        ) : (
          <SEOSettingsForm
            seoSetting={selectedSetting}
            onSave={handleSave}
            onCancel={() => {
              setCurrentView("list");
              setSelectedSetting(null);
            }}
          />
        )}

        {showModal && selectedSetting && (
          <SEOSettingsModal
            seoSetting={selectedSetting}
            onClose={() => {
              setShowModal(false);
              setSelectedSetting(null);
            }}
          />
        )}
      </div>
    </>
  );
};

export default SEOPortal;
